import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, MapPin, Phone, Share2, X } from 'lucide-react';
import { Button } from '@ridemaadi/ui';
import { geofenceService } from '../../navigation/services/GeofenceService';
import { useClearSos, type SosAlert } from '../activity';

/** Post-SOS emergency status sheet for the rider who sent the alert. */
export function SosEmergencyStatusSheet({
  rideId,
  alert,
  open,
  onClose,
  onCleared,
}: {
  rideId: string;
  alert: SosAlert | null;
  open: boolean;
  onClose: () => void;
  onCleared: () => void;
}) {
  const clear = useClearSos(rideId);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !alert) return null;

  const shareLocation = async () => {
    try {
      const pos = await geofenceService.getCurrentPosition();
      const url = `https://maps.google.com/?q=${pos.lat},${pos.lng}`;
      if (navigator.share) {
        await navigator.share({ title: 'My live location', url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      /* user cancelled or unavailable */
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-t-2xl border-t border-border bg-surface shadow-2xl"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sos-status-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 pb-2 pt-3">
          <h2 id="sos-status-title" className="text-base font-semibold text-danger">
            SOS Active
          </h2>
          <button
            type="button"
            className="rounded-lg p-2 text-muted hover:bg-surface-alt"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-4 pb-4">
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden /> SOS Sent
            </li>
            <li className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden /> Captain Notified
            </li>
            <li className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden /> Riders Notified
            </li>
            <li className="flex items-center gap-2 text-content">
              <MapPin className="h-4 w-4 text-primary" aria-hidden />
              {alert.responseCount} rider{alert.responseCount === 1 ? '' : 's'} responding
            </li>
          </ul>

          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              variant="secondary"
              fullWidth
              loading={clear.isPending}
              onClick={() => void clear.mutateAsync().then(onCleared)}
            >
              I&apos;m OK
            </Button>
            <a href="tel:100" className="inline-flex">
              <Button variant="secondary" fullWidth className="w-full">
                <Phone className="h-4 w-4" aria-hidden /> Call Police
              </Button>
            </a>
            <Button variant="secondary" fullWidth className="sm:col-span-2" onClick={() => void shareLocation()}>
              <Share2 className="h-4 w-4" aria-hidden /> Share Live Location
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
